import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Api } from '../../api/api';
import {
  getEnergyPrices,
  getWeatherInputData,
  getHistoricalTrainingInputData,
  getSystemParameters,
  receiveOptimizerOutput,
} from '../../api/functions';
import { LocationStateService } from '../../common/services/location-state.service';
import { OptimizationSolutionEntryRequest } from '../../api/models/optimization-solution-entry-request';

type EndpointKey =
  | 'forecastInput.getWeatherInputData'
  | 'forecastInput.getHistoricalTrainingInputData'
  | 'optimizer.getSystemParameters'
  | 'optimizer.getEnergyPrices'
  | 'optimizer.receiveOptimizerOutput';

type DateRangeEndpointKey = Exclude<EndpointKey, 'optimizer.receiveOptimizerOutput'>;

interface DateRangePayload {
  startDate: string;
  endDate: string;
  source?: string;
}

@Component({
  selector: 'app-optimizer-forecast',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
  ],
  templateUrl: './optimizer-forecast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./optimizer-forecast.component.css'],
})
export class OptimizerForecastComponent {
  private readonly api = inject(Api);
  protected readonly locationState = inject(LocationStateService);

  readonly selectedLocationId = computed(() => this.locationState.currentLocation()?.id ?? '');
  readonly selectedLocationName = computed(() => this.locationState.currentLocation()?.name ?? '');

  readonly dateRangeJsons = signal<Record<DateRangeEndpointKey, string>>({
    'forecastInput.getWeatherInputData': '',
    'forecastInput.getHistoricalTrainingInputData': '',
    'optimizer.getSystemParameters': '',
    'optimizer.getEnergyPrices': '',
  });
  readonly optimizerResultJson = signal<string>('');

  readonly responses = signal<Partial<Record<EndpointKey, unknown>>>({});
  readonly errors = signal<Partial<Record<EndpointKey, string>>>({});
  readonly loading = signal<Partial<Record<EndpointKey, boolean>>>({});

  readonly endpointDocs = [
    {
      group: 'Forecast input',
      items: [
        {
          key: 'forecastInput.getWeatherInputData' as const,
          name: 'Load weather input data',
          method: 'GET',
          path: getWeatherInputData.PATH,
          input: ['path `location_id` (selected above)', 'query `from`/`to`/`source` from the request JSON below'],
        },
        {
          key: 'forecastInput.getHistoricalTrainingInputData' as const,
          name: 'Load historical training input data',
          method: 'GET',
          path: getHistoricalTrainingInputData.PATH,
          input: ['path `location_id` (selected above)', 'query `from`/`to`/`weatherDataSource` (from JSON `source`) below'],
        },
      ],
    },
    {
      group: 'Optimizer',
      items: [
        {
          key: 'optimizer.getSystemParameters' as const,
          name: 'Load optimizer system parameters',
          method: 'GET',
          path: getSystemParameters.PATH,
          input: ['path `location_id` (selected above)', 'query `from`/`to` from the request JSON below'],
        },
        {
          key: 'optimizer.getEnergyPrices' as const,
          name: 'Load optimizer energy prices',
          method: 'GET',
          path: getEnergyPrices.PATH,
          input: ['path `location_id` (selected above)', 'query `from`/`to` from the request JSON below'],
        },
        {
          key: 'optimizer.receiveOptimizerOutput' as const,
          name: 'Submit optimizer results',
          method: 'POST',
          path: receiveOptimizerOutput.PATH,
          input: ['path `location_id` (selected above)', 'textarea `optimizerResultJson`: `ForecastOptimizationResult`'],
        },
      ],
    },
  ] as const;

  constructor() {
    effect(() => {
      this.dateRangeJsons.set(this.buildDefaultDateRangeJsons());
      this.optimizerResultJson.set(JSON.stringify(this.buildDefaultOptimizerResult(), null, 2));
    });
  }

  setLocationId(locationId: string): void {
    const next = this.locationState.locations().find((location) => location.id === locationId);
    if (next) {
      this.locationState.setLocation(next);
    }
  }

  clearAll(): void {
    this.responses.set({});
    this.errors.set({});
    this.loading.set({});
  }

  async call(endpoint: EndpointKey): Promise<void> {
    const locationId = this.selectedLocationId();
    if (!locationId) {
      this.setError(endpoint, 'No location selected.');
      return;
    }

    this.setLoading(endpoint, true);
    this.setError(endpoint, null);
    this.setResponse(endpoint, null);

    try {
      if (endpoint === 'forecastInput.getWeatherInputData') {
        const parsed = this.parseDateRange(endpoint);
        const res = await this.api.invoke(getWeatherInputData, {
          location_id: locationId,
          from: parsed.startDate,
          to: parsed.endDate,
          source: parsed.source,
        });
        this.setResponse(endpoint, res);
        return;
      }

      if (endpoint === 'forecastInput.getHistoricalTrainingInputData') {
        const parsed = this.parseDateRange(endpoint);
        const res = await this.api.invoke(getHistoricalTrainingInputData, {
          location_id: locationId,
          from: parsed.startDate,
          to: parsed.endDate,
          weatherDataSource: parsed.source,
        });
        this.setResponse(endpoint, res);
        return;
      }

      if (endpoint === 'optimizer.getSystemParameters') {
        const parsed = this.parseDateRange(endpoint);
        const res = await this.api.invoke(getSystemParameters, {
          location_id: locationId,
          from: parsed.startDate,
          to: parsed.endDate,
        });
        this.setResponse(endpoint, res);
        return;
      }

      if (endpoint === 'optimizer.getEnergyPrices') {
        const parsed = this.parseDateRange(endpoint);
        const res = await this.api.invoke(getEnergyPrices, {
          location_id: locationId,
          from: parsed.startDate,
          to: parsed.endDate,
        });
        this.setResponse(endpoint, res);
        return;
      }

      if (endpoint === 'optimizer.receiveOptimizerOutput') {
        const body = this.parseJson<OptimizationSolutionEntryRequest>(
          this.optimizerResultJson(),
          'Optimizer result JSON',
        );
        const res = await this.api.invoke(receiveOptimizerOutput, { location_id: locationId, body });
        this.setResponse(endpoint, res ?? { status: 'submitted' });
        return;
      }

      this.setError(endpoint, `Unknown endpoint key: ${endpoint}`);
    } catch (error) {
      this.setError(endpoint, this.formatError(error));
    } finally {
      this.setLoading(endpoint, false);
    }
  }

  isEndpointDisabled(endpoint: EndpointKey): boolean {
    void endpoint;
    return false;
  }

  isDateRangeEndpoint(endpoint: EndpointKey): endpoint is DateRangeEndpointKey {
    return endpoint !== 'optimizer.receiveOptimizerOutput';
  }

  updateDateRangeJson(endpoint: DateRangeEndpointKey, value: string): void {
    this.dateRangeJsons.update((curr) => ({ ...curr, [endpoint]: value }));
  }

  private buildDefaultDateRangeJsons(): Record<DateRangeEndpointKey, string> {
    return {
      'forecastInput.getWeatherInputData': this.stringifyDateRange({
        ...this.buildTomorrowDateRange(),
      }),
      'forecastInput.getHistoricalTrainingInputData': this.stringifyDateRange({
        ...this.buildHistoricalTrainingDateRange(),
      }),
      'optimizer.getSystemParameters': this.stringifyDateRange(this.buildTomorrowDateRange()),
      'optimizer.getEnergyPrices': this.stringifyDateRange(this.buildTomorrowDateRange()),
    };
  }

  private buildTomorrowDateRange(): DateRangePayload {
    const tomorrow = this.addDays(new Date(), 1);
    return {
      startDate: this.startOfDay(tomorrow).toISOString(),
      endDate: this.endOfDay(tomorrow).toISOString(),
    };
  }

  private buildHistoricalTrainingDateRange(): DateRangePayload {
    const yesterday = this.addDays(new Date(), -1);
    const startDate = this.startOfDay(this.addMonths(yesterday, -6));
    const endDate = this.endOfDay(yesterday);
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }

  private stringifyDateRange(dateRange: DateRangePayload): string {
    return JSON.stringify(dateRange, null, 2);
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  private addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }

  private buildDefaultOptimizerResult(): OptimizationSolutionEntryRequest {
    return {
      optimizationTerminationStatus: 'optimal',
      optimizationSolution: {
        costs: 0,
        peakFeedin: 0,
        timesteps: [],
      },
      simulationResult: {
        Charge: [],
        Consumption: [],
        Costs: 0,
        Discharge: [],
        Feedin: [],
        Soc: [],
      },
    };
  }

  private parseDateRange(endpoint: DateRangeEndpointKey): DateRangePayload {
    const json = this.dateRangeJsons()[endpoint];
    const parsed = this.parseJson<DateRangePayload>(json, 'Date range JSON');
    if (!parsed.startDate || !parsed.endDate) {
      throw new Error('Date range requires `startDate` and `endDate`.');
    }
    return parsed;
  }

  private parseJson<T>(value: string, label: string): T {
    try {
      return JSON.parse(value) as T;
    } catch {
      throw new Error(`${label} is invalid JSON.`);
    }
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown error.';
    }
  }

  private setLoading(endpoint: EndpointKey, isLoading: boolean): void {
    this.loading.update((curr) => ({ ...curr, [endpoint]: isLoading }));
  }

  private setError(endpoint: EndpointKey, error: string | null): void {
    this.errors.update((curr) => {
      const next = { ...curr };
      if (error) {
        next[endpoint] = error;
      } else {
        delete next[endpoint];
      }
      return next;
    });
  }

  private setResponse(endpoint: EndpointKey, response: unknown | null): void {
    this.responses.update((curr) => {
      const next = { ...curr };
      if (response === null) {
        delete next[endpoint];
      } else {
        next[endpoint] = response;
      }
      return next;
    });
  }
}
